import { ResourceListCard } from "#web/components/common/ResourceListCard/ResourceListCard";
import { i18nLike } from "#web/services/i18nLike";
import {
  Icon,
  IndexFilters,
  IndexFiltersMode,
  IndexTable,
  PaginationProps,
  TabProps,
  Text,
  Tooltip,
  useBreakpoints,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";

import { LeakedCodes } from "../LeakedCodePageQuery";
import { InitialScanInProgressEmptyState } from "./InitialScanInProgressEmptyState";
import { LeakedCodesIndexTableRow } from "./LeakedCodesIndexTableRow";
import { LeakedCodesIndexTableRowsSkeleton } from "./LeakedCodesIndexTableRowsSkeleton";
import { NoLeakedCodesEmptyState } from "./NoLeakedCodesEmptyState";
import * as s from "./styles.css";
import { en } from "./translations";

interface LeakedCodesIndexTableProps {
  isInitialScrapingComplete: boolean;
  leakedCodes: LeakedCodes["leakedCodes"];
  loading: boolean;
  onQueryChange: (value: string) => void;
  onQueryClear: () => void;
  pagination: PaginationProps;
  queryValue: string;
  selected: number;
  shopCurrency: string;
  tabs: TabProps[];
}

export const LeakedCodesIndexTable = ({
  isInitialScrapingComplete,
  leakedCodes,
  loading,
  onQueryChange,
  onQueryClear,
  pagination,
  queryValue,
  selected,
  shopCurrency,
  tabs,
}: LeakedCodesIndexTableProps) => {
  const { smDown } = useBreakpoints();
  const t = i18nLike(en);

  const { mode, setMode } = useSetIndexFiltersMode();

  return (
    <div className={s.LeakedCodesIndexTable}>
      <ResourceListCard>
        <IndexFilters
          cancelAction={{
            onAction: () => {
              setMode(IndexFiltersMode.Default);
            },
          }}
          canCreateNewView={false}
          filters={[]}
          hideFilters
          mode={mode}
          onClearAll={onQueryClear}
          onQueryChange={onQueryChange}
          onQueryClear={onQueryClear}
          queryPlaceholder={t("search.placeholder")}
          queryValue={queryValue}
          selected={selected}
          setMode={setMode}
          tabs={tabs}
        />
        <IndexTable
          condensed={smDown}
          emptyState={isInitialScrapingComplete ? <NoLeakedCodesEmptyState /> : <InitialScanInProgressEmptyState />}
          headings={[
            {
              id: "leakedCode",
              title: (
                <Text as="span" fontWeight="medium" variant="headingXs">
                  {t("leakedCode.singular")}
                </Text>
              ),
            },
            {
              id: "leak-status",
              title: (
                <div className={s.HeadingTooltipWrapper}>
                  <Text as="span" fontWeight="medium" variant="headingXs">
                    {t("leakStatus.title")}
                  </Text>
                  <Tooltip content={t("leakStatus.tooltip")}>
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                </div>
              ),
            },
            {
              id: "shopify-status",
              title: (
                <div className={s.HeadingTooltipWrapper}>
                  <Text as="span" fontWeight="medium" variant="headingXs">
                    {t("shopifyStatus.title")}
                  </Text>
                  <Tooltip content={t("shopifyStatus.tooltip")}>
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                </div>
              ),
            },
            {
              id: "value",
              title: t("value.title"),
            },
            {
              id: "avg-usage-before-leak",
              title: (
                <div className={s.HeadingTooltipWrapper}>
                  <Text as="span" fontWeight="medium" variant="headingXs">
                    {t("avgUsageBeforeLeak.title")}
                  </Text>
                  <Tooltip content={t("avgUsageBeforeLeak.tooltip")}>
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                </div>
              ),
            },
            {
              id: "avg-usage-after-leak",
              title: (
                <div className={s.HeadingTooltipWrapper}>
                  <Text as="span" fontWeight="medium" variant="headingXs">
                    {t("avgUsageAfterLeak.title")}
                  </Text>
                  <Tooltip content={t("avgUsageAfterLeak.tooltip")}>
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                </div>
              ),
            },
            {
              id: "sources",
              title: (
                <div className={s.HeadingTooltipWrapper}>
                  <Text as="span" fontWeight="medium" variant="headingXs">
                    {t("sources.title")}
                  </Text>
                  <Tooltip content={t("sources.tooltip")}>
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                </div>
              ),
            },
            {
              id: "first-seen",
              title: (
                <div className={s.HeadingTooltipWrapper}>
                  <Text as="span" fontWeight="medium" variant="headingXs">
                    {t("firstSeen.title")}
                  </Text>
                  <Tooltip content={t("firstSeen.tooltip")}>
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                </div>
              ),
            },
          ]}
          itemCount={loading ? 1 : leakedCodes.length}
          loading={loading}
          pagination={pagination}
          resourceName={{
            plural: t("leakedCode.plural"),
            singular: t("leakedCode.singular"),
          }}
          selectable={false}
        >
          {loading && <LeakedCodesIndexTableRowsSkeleton />}
          {leakedCodes.map((leakedCode, index) => (
            <LeakedCodesIndexTableRow
              key={leakedCode.id}
              leakedCode={leakedCode}
              position={index}
              shopCurrency={shopCurrency}
            />
          ))}
        </IndexTable>
      </ResourceListCard>
    </div>
  );
};
